// src/Transcribe.js
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { MicrophoneIcon } from "@heroicons/react/20/solid";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const StreamingPoint = process.env.REACT_APP_API_STREAMING;

console.log(StreamingPoint)

class Transcribe extends React.Component {
  state = {
    isRecording: false,
    status: "idle",
    sessionId: uuidv4(),
    transcripts: [],          // [{ sequence, text }]
    currentMediaRecorder: null,
    showPopup: false,
    showTooltip: false,
    // new for streaming
    livePreview: "",
  };

  static defaultProps = {
    onInputUpdate: () => {},
  };

  // instance fields (not in state)
  stream = null;
  mediaRecorder = null;
  ws = null;
  sentToParentOnce = false;
  recordingEnded = false;
  finalTranscriptCache = "";

  componentWillUnmount() {
    // Clean up if user navigates away
    this.stop(false);
  }

  getWebSocketUrl() {
      if (StreamingPoint) return StreamingPoint;

  // safety fallback
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  return isLocal
    ? "ws://localhost:4000/transcribe"
    : `wss://${window.location.host}/transcribe`;
  }

  start = async () => {
    if (this.state.isRecording) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setState({ status: "getUserMedia not supported" });
      toast.error("Microphone not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.stream = stream;

      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        toast.info("Recording started…", { autoClose: 1000 });
        this.sentToParentOnce = false;
        this.recordingEnded = false;
        this.finalTranscriptCache = "";

        this.setState(
          {
            isRecording: true,
            status: "recording",
            livePreview: "",
            showPopup: true,
          },
          () => {
            this.startRecorder();
          }
        );
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          if (msg.type === "transcript") {
            const { text, isFinal } = msg;

            // PARTIAL (fast, streaming)
            if (!isFinal) {
              this.setState({ livePreview: text });  // update every 0.5–1s
              return;
            }

            // FINAL (confirmed)
            this.setState(
              (prev) => ({
                livePreview: "",
                transcripts: [
                  ...prev.transcripts,
                  {
                    sequence: prev.transcripts.length + 1,
                    text,
                    jobName: "streaming",
                  },
                ],
              }));
          } else if (msg.type === "info") {
            console.info("Transcribe WS info:", msg.message);
          } else if (msg.type === "warning") {
            console.warn("Transcribe WS warning:", msg.message);
          } else if (msg.type === "error") {
            console.error("Transcribe WS error:", msg.message);
            this.setState({ status: "transcribe error" });
            toast.error("Transcription error.");
          }
        } catch (e) {
          console.error("WS parse error:", e);
        }
      };

      this.ws.onerror = (err) => {

        this.setState({ status: "WebSocket error" });
        toast.error("WebSocket connection error.");
      };

      this.ws.onclose = () => {
        // recording has ended, cache final transcript
        this.recordingEnded = true;
        this.finalTranscriptCache = this.combinedTranscript();

        // Only send after popup is closed
        if (!this.state.showPopup) {
          this.sendFinalToParentOnce();
        }

        this.ws = null;
      };


    } catch (err) {
      this.setState({ status: "mic permission denied or unsupported" });
      toast.error("Microphone permission denied.");
    }
  };

  startRecorder = () => {
    if (!this.stream || !this.state.isRecording) return;

    try {
      // Let the browser choose its preferred format (usually WebM+Opus)
      this.mediaRecorder = new MediaRecorder(this.stream);
    } catch (e) {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
      return;
    }


    this.setState({ currentMediaRecorder: this.mediaRecorder });

    this.mediaRecorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      e.data.arrayBuffer().then((buf) => {
        try {
          this.ws.send(buf);   // binary WebM to backend (ffmpeg will decode it)
        } catch (err) {
          console.error("WS send error:", err);
        }
      });
    };

    this.mediaRecorder.onerror = (e) => {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
    };

    this.mediaRecorder.onstop = () => {
      this.setState({ currentMediaRecorder: null });
    };

    // Request audio chunks every 250ms
    this.mediaRecorder.start(250);
  };

  /**
   * stop(showPopup = true)
   * - showPopup true when user manually stops
   * - showPopup false when unmounting
   */
  stop = (showPopup = true) => {
    if (!this.state.isRecording && !this.stream && !this.ws) return;

    this.setState({
      isRecording: false,
      status: "stopped",
      showPopup: showPopup ? true : this.state.showPopup,
    });

    // Stop MediaRecorder
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }

    // Stop the audio stream
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    // Tell backend we're done
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: "end" }));
      } catch (e) {
        console.error("WS send end error:", e);
      }

      setTimeout(() => {
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }
      }, 500);
    } else if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    toast.info("Recording stopped.", { autoClose: 1000 });
  };

  sendFinalToParentOnce = () => {
    if (this.sentToParentOnce) return;

    const full = (this.finalTranscriptCache || this.combinedTranscript()).trim();
    if (!full) return;

    this.sentToParentOnce = true;
    this.props.onInputUpdate({ transcription: full, output: null });
  };


  toggleRecording = () => {
    if (this.state.isRecording) {
      this.stop(true);
    } else {
      this.start();
    }
  };

  combinedTranscript() {
    const { transcripts } = this.state;
    return transcripts
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((t) => t.text)
      .join(" ")
      .trim();
  }

  renderPopup() {
    const { showPopup, isRecording, currentMediaRecorder } = this.state;
    if (!showPopup) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-4">
          {/* TOP: mic + waveform */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                className={`${
                  isRecording
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-sky-800 hover:bg-sky-900"
                } flex items-center text-white px-3 py-2 rounded-3xl hover:shadow-xl`}
                onClick={this.toggleRecording}
              >
                <MicrophoneIcon className="w-6 h-6" />
                {isRecording && <span className="ml-2 text-sm">Recording</span>}
              </button>

              <div style={{ width: 200, height: 40 }}>
                {isRecording && currentMediaRecorder ? (
                  <LiveAudioVisualizer
                    mediaRecorder={currentMediaRecorder}
                    width={200}
                    height={40}
                    barWidth={3}
                    gap={2}
                    barColor={"#205680"}
                  />
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                // if still recording, stop first (WS will close and cache transcript)
                if (this.state.isRecording) {
                  this.stop(false);
                }

                this.setState({ showPopup: false }, () => {
                  // if recording already ended, send now
                  if (this.recordingEnded) {
                    this.sendFinalToParentOnce();
                  }
                });
              }}
              className="bg-sky-800 hover:bg-sky-900 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Finish 
            </button>
          </div>

          {/* TITLE */}
          <h3 className="text-lg font-semibold mb-2">Transcript</h3>

          {/* TEXTAREA */}
          <textarea
            className="w-full border border-slate-100 rounded-md p-2 min-h-[600px] text-md resize-none"
            readOnly
            value= {(this.combinedTranscript() + (this.state.livePreview ? " " + this.state.livePreview : "")).trim()}
            placeholder="Transcript will appear here as you speak…"
          />
        </div>
      </div>
    );
  }

  render() {
    const { isRecording, showTooltip } = this.state;

    return (
      <>
        {/* Optional: keep external mic trigger if you like */}
        <div className="flex flex-col items-center m-4 h-auto">
          <div className="flex items-center space-x-4 sm:ml-[700px] relative">
            <button
              id="microphone"
              className={`${
                isRecording
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-sky-800 hover:bg-sky-900"
              } flex items-center text-white px-2 py-2 rounded-3xl hover:shadow-xl relative`}
              onClick={this.toggleRecording}
              onMouseEnter={() => this.setState({ showTooltip: true })}
              onMouseLeave={() => this.setState({ showTooltip: false })}
            >
              <MicrophoneIcon className="w-8 h-8" />
              {isRecording && <p className="px-2">Recording</p>}
            </button>

            {showTooltip && (
              <div className="absolute ml-80 -mt-20">
                <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 -ml-10 shadow">
                  Voice recording
                </div>
              </div>
            )}
          </div>
        </div>

        {this.renderPopup()}
        <ToastContainer />
      </>
    );
  }
}

export default Transcribe;
